import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Plus, Clock } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { EmptyState, SkeletonLoader } from "@/components/system";
import { formatShortDate } from "@/lib/date-format";

/**
 * /threads (SPEC 1.2.9) — list of all open conversation arcs. Sorted by
 * most-recent thread_event first.
 */

type ThreadRow = {
  id: string;
  title: string;
  kind: string;
  status: string;
  linked_person_ids: string[];
  next_action: string | null;
  next_action_due: string | null;
  last_event_ts: string | null;
  updated_at: string;
};

type PersonLite = { id: string; display_name: string };

export function Threads() {
  const { session } = useSession();
  const userId = session?.user?.id;

  const q = useQuery<{ threads: ThreadRow[]; people: Map<string, PersonLite> }>({
    queryKey: ["threads_list", userId],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: async () => {
      if (!userId) return { threads: [], people: new Map() };
      const client = supabase as unknown as {
        from: (t: string) => {
          select: (cols: string) => {
            eq: (col: string, v: string) => Promise<{ data: unknown[] | null; error: unknown }>;
          };
        };
      };
      const [tRes, pRes] = await Promise.all([
        client
          .from("thread")
          .select(
            "id, title, kind, status, linked_person_ids, next_action, next_action_due, last_event_ts, updated_at",
          )
          .eq("user_id", userId),
        client.from("person").select("id, display_name").eq("user_id", userId),
      ]);
      if (tRes.error) throw tRes.error as Error;
      if (pRes.error) throw pRes.error as Error;
      const threads = ((tRes.data ?? []) as ThreadRow[]).slice().sort((a, b) => {
        const ats = a.last_event_ts ?? a.updated_at;
        const bts = b.last_event_ts ?? b.updated_at;
        return bts.localeCompare(ats);
      });
      const people = new Map<string, PersonLite>(
        ((pRes.data ?? []) as PersonLite[]).map((p) => [p.id, p]),
      );
      return { threads, people };
    },
  });

  return (
    <article aria-labelledby="threads-heading">
      <header
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: "var(--space-4)",
          marginBottom: "var(--space-6)",
        }}
      >
        <div>
          <p
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: "var(--type-micro)",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "var(--fg-3)",
              margin: 0,
            }}
          >
            Threads
          </p>
          <h1
            id="threads-heading"
            style={{
              fontFamily: "var(--font-serif)",
              fontStyle: "italic",
              fontWeight: 500,
              fontSize: "var(--type-display-m)",
              margin: "var(--space-2) 0 0",
            }}
          >
            What's open with whom.
          </h1>
        </div>
        <div style={{ display: "flex", gap: "var(--space-2)" }}>
          <Link
            to="/people"
            style={{
              padding: "8px 14px",
              background: "transparent",
              color: "var(--fg-2)",
              border: "1px solid var(--hair-2)",
              borderRadius: "var(--radius-card)",
              fontSize: "var(--type-body-s)",
              textDecoration: "none",
            }}
          >
            People
          </Link>
          <Link
            to="/threads/new"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 14px",
              background: "var(--accent)",
              color: "#0a0b0d",
              fontWeight: 500,
              fontSize: "var(--type-body-s)",
              borderRadius: "var(--radius-card)",
              textDecoration: "none",
              whiteSpace: "nowrap",
            }}
          >
            <Plus size={14} strokeWidth={1.8} /> New thread
          </Link>
        </div>
      </header>

      {q.isLoading ? (
        <SkeletonLoader variant="card" count={3} height={88} />
      ) : !q.data || q.data.threads.length === 0 ? (
        <EmptyState
          headline="No threads yet."
          sub="Start one to track an ongoing conversation arc."
        />
      ) : (
        <ul
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "grid",
            gap: "var(--space-2)",
          }}
        >
          {q.data.threads.map((t) => (
            <li key={t.id}>
              <ThreadRow row={t} people={q.data.people} />
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

function ThreadRow({ row, people }: { row: ThreadRow; people: Map<string, PersonLite> }) {
  const names = (row.linked_person_ids ?? [])
    .map((pid) => people.get(pid)?.display_name)
    .filter((n): n is string => Boolean(n));
  return (
    <Link
      to={`/threads/${row.id}`}
      style={{
        display: "grid",
        gridTemplateColumns: "1fr max-content",
        gap: "var(--space-3)",
        padding: "var(--space-4)",
        background: "var(--bg-2)",
        border: `1px solid ${row.status === "awaiting_me" ? "var(--amber)" : "var(--hair)"}`,
        borderRadius: "var(--radius-card)",
        textDecoration: "none",
        color: "var(--fg)",
        alignItems: "center",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
          <span style={{ fontSize: "var(--type-body)", fontWeight: 500, color: "var(--fg)" }}>
            {row.title}
          </span>
          <StatusPill status={row.status} />
        </div>
        <div
          style={{
            fontSize: "var(--type-meta)",
            color: "var(--fg-3)",
            marginTop: 2,
          }}
        >
          {names.length > 0 ? names.join(", ") : "—"}
          {row.next_action && (
            <>
              {" · next: "}
              <span style={{ color: "var(--fg-2)" }}>{row.next_action}</span>
              {row.next_action_due && (
                <> by {formatShortDate(row.next_action_due)}</>
              )}
            </>
          )}
        </div>
      </div>
      <div
        style={{
          fontSize: "var(--type-meta)",
          color: "var(--fg-3)",
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          whiteSpace: "nowrap",
        }}
      >
        <Clock size={12} strokeWidth={1.5} />
        {row.last_event_ts ? formatShortDate(row.last_event_ts) : "—"}
      </div>
    </Link>
  );
}

function StatusPill({ status }: { status: string }) {
  const tone =
    status === "awaiting_me"
      ? { fg: "var(--amber)", bg: "color-mix(in oklab, var(--amber) 14%, transparent)" }
      : status === "awaiting_them"
        ? { fg: "var(--fg-3)", bg: "var(--bg-3)" }
        : status === "closed"
          ? { fg: "var(--fg-3)", bg: "var(--bg-3)" }
          : status === "paused"
            ? { fg: "var(--fg-3)", bg: "var(--bg-3)" }
            : { fg: "var(--accent)", bg: "var(--accent-wash)" };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 6px",
        background: tone.bg,
        color: tone.fg,
        fontSize: "var(--type-micro)",
        fontWeight: 600,
        borderRadius: "var(--radius-chip)",
        letterSpacing: "0.04em",
        textTransform: "uppercase",
      }}
    >
      {status.replace(/_/g, " ")}
    </span>
  );
}
